import { useState, useCallback, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { LoadingSpinner } from '../LoadingSpinner';
import { useProjectContext } from '../../contexts/ProjectContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { motion } from "framer-motion";
import useEscapeKey from '../../hooks/useEscapeKey';
import { useClickOutside } from '../../hooks/useClickOutside'; // Added for click outside
import ValidatedInput from '../shared/ValidatedInput';
import { DEFAULT_VALIDATION_CONFIG, validateAndSanitizeInput } from '../../utils/validation/inputValidation';
import useStore from '../../store/useStore';
import { projectService } from '../../services/project.service';
import { useAuth } from '../../hooks/useAuth';
import { Project as ProjectClass } from '../../types';

interface SaveAsDialogProps {
  open: boolean;
  onClose: () => void;
  originalName: string;
  originalDescription?: string;
}

export default function SaveAsDialog({
  open,
  onClose,
  originalName,
  originalDescription = '',
}: SaveAsDialogProps) {
  const [name, setName] = useState(validateAndSanitizeInput(`${originalName} (Copy)`, DEFAULT_VALIDATION_CONFIG.projectName));
  const [description, setDescription] = useState(validateAndSanitizeInput(originalDescription, DEFAULT_VALIDATION_CONFIG.projectDescription));
  const [nameError, setNameError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { showNotification } = useNotifications();
  const nameInputRef = useRef<HTMLInputElement>(null);
  // dialogContentRef will be initialized by useClickOutside
  // const dialogContentRef = useRef<HTMLDivElement>(null);

  const { project: currentProject, loadProject } = useProjectContext();
  const { user } = useAuth();
  const { nodes, edges } = useStore(state => ({ nodes: state.nodes, edges: state.edges }));

  // Reset fields when dialog opens with a new originalName or originalDescription
  useEffect(() => {
    if (open) {
      setName(validateAndSanitizeInput(`${originalName} (Copy)`, DEFAULT_VALIDATION_CONFIG.projectName));
      setDescription(validateAndSanitizeInput(originalDescription, DEFAULT_VALIDATION_CONFIG.projectDescription));
      setNameError('');
      setDescriptionError('');
      // Focus input when dialog opens
      if (nameInputRef.current) {
        nameInputRef.current.focus();
        nameInputRef.current.select();
      }
    }
  }, [open, originalName, originalDescription]);

  const handleClose = () => {
    onClose();
  };

  const handleSaveAsSubmit = useCallback(async () => {
    const sanitizedName = validateAndSanitizeInput(name, DEFAULT_VALIDATION_CONFIG.projectName);
    const sanitizedDescription = validateAndSanitizeInput(description, DEFAULT_VALIDATION_CONFIG.projectDescription);

    if (!sanitizedName.trim()) {
      setNameError('Please enter a project name');
      return;
    }

    setIsLoading(true);
    try {
      if (!user) {
        showNotification('error', 'You must be logged in to save a project.');
        setIsLoading(false);
        return;
      }

      // Deselect nodes and edges before saving
      const deselectedNodes = nodes.map(node => ({ ...node, selected: false }));
      const deselectedEdges = edges.map(edge => ({ ...edge, selected: false }));

      const newProjectData = new ProjectClass({
        id: 'unsaved-project',
        name: sanitizedName,
        description: sanitizedDescription,
        nodes: deselectedNodes,
        edges: deselectedEdges,
        owner_id: user.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        shared_with: [],
        is_locked: false,
        is_archived: false,
        version: 1,
        settings: currentProject?.settings || {},
      });

      const createdProject = await projectService.saveProject(newProjectData, user.id);
      await loadProject(createdProject.id);

      showNotification('success', `Project saved as "${createdProject.name}"`);
      handleClose(); // Close the dialog
    } catch (error) {
      console.error('Error saving project as:', error);
      showNotification('error', 'Failed to save project as new.');
    } finally {
      setIsLoading(false);
    }
  }, [name, description, nodes, edges, user, currentProject, loadProject, showNotification, onClose, handleClose]);

  // Focus management is now part of the useEffect that resets fields on open

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (name.trim()) {
        handleSaveAsSubmit();
      }
    }
  };
  
  useEscapeKey(handleClose); // Use escape key to close the dialog
  const dialogContentRef = useClickOutside<HTMLDivElement>(handleClose); // Corrected usage

  // AnimatePresence is expected to be handled by the parent component (e.g., ProjectMenuDialogs)
  // This component now directly returns the motion.divs if 'open' is true.
  // The parent's AnimatePresence will handle the enter/exit animations.
  if (!open) {
    return null;
  }

  return (
    <motion.div
      className="fixed inset-0 bg-gray-950/50 dark:bg-gray-950/70 flex items-center justify-center z-[200]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }} // This is for the backdrop fade

    >
      <motion.div
        ref={dialogContentRef} // Assign ref here
        className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-[480px] mx-4 border border-gray-200 dark:border-gray-700"
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }} // This is for the dialog itself
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="px-6 pt-5 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Save Project As</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-300 dark:text-gray-500 dark:hover:text-gray-400"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-4">
            <ValidatedInput
              id="new-project-name"
              label="New Project Name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError('');
              }}
              validationConfig={DEFAULT_VALIDATION_CONFIG.projectName}
              placeholder="Enter new project name"
              // autoFocus is handled by useEffect focusing nameInputRef
              errorMessage={nameError}
              onBlur={() => {
                if (!name.trim()) {
                  setNameError('Project name is required');
                }
              }}
              // Pass the ref to the underlying input if ValidatedInput supports it
              // For example, if ValidatedInput forwards refs: ref={nameInputRef}
              // Or if it has a specific prop: inputRef={nameInputRef}
              // Removing ref={nameInputRef} as ValidatedInput might not support it directly.
              // The useEffect for focus will still attempt to use nameInputRef.current.
            />
            <ValidatedInput
              id="new-project-description"
              label="Description (Optional)"
              isTextarea={true}
              value={description}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                setDescription(e.target.value);
                if (descriptionError) setDescriptionError('');
              }}
              validationConfig={DEFAULT_VALIDATION_CONFIG.projectDescription}
              placeholder="Enter a brief description for the new project"
              rows={3}
              errorMessage={descriptionError}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 rounded-b-lg border-t border-gray-200 dark:border-gray-800">
          <div className="flex justify-end gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAsSubmit}
              disabled={!name.trim() || isLoading}
              className={`
            px-4 py-2 text-sm font-medium rounded-md inline-flex items-center gap-2
            ${
              !name.trim() || isLoading
                ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }
          `}
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="sm" className="text-gray-500 dark:text-gray-400" />
                  <span>Saving...</span>
                </>
              ) : (
                'Save As'
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
